import React, { useEffect, useState } from "react";
import Moment from "moment";
import { useParams } from "react-router-dom";
import {
  Box,
  HStack,
  Image,
  Pressable,
  Text,
  Tooltip,
  VStack,
  Divider,
  Collapse,
  ScrollView,
} from "native-base";
import {
  LocationIcon,
  TimerIcon,
  ArrowActiveIcon,
  ArrowInActiveIcon,
} from "../../assets/icons/icon-assets";
import { Loc } from "../../App/Loc";
import * as AV from "../../AVCore/AVCore";
import { Location } from "../../AVCore/Location";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../types_interface/RootState";
import { setWeatherData } from "../../Redux/Actions/userActions";
import EntityProfilePic from "../../components/common/EntityProfilePic";

// Weather icons
import PartlyCloudys from "../../assets/images/PartlyCloudys.png";
import PatchyFog from "../../assets/images/PatchyFog.png";
import ChanceShowersAndThunderstorms from "../../assets/images/ChanceShowersAndThunderstorms.png";
import Rainy from "../../assets/images/Rainy.png";
import MostlyClear from "../../assets/images/MostlyClear.png";
import SlightChanceRainShowers from "../../assets/images/SlightChanceRainShowers.png";
import MostlyCloudy from "../../assets/images/MostlyCloudy.png";
import ChanceSnowShowersthenSunny from "../../assets/images/ChanceSnowShowersthenSunny.png";
import ChanceSnowShowers from "../../assets/images/ChanceSnowShowers.png";
import ChanceRainAndSnowthenCloudy from "../../assets/images/ChanceRainAndSnowthenCloudy.png";
import SlightChanceLightRain from "../../assets/images/SlightChanceLightRain.png";
import PartlySunnythenChanceShowersAndThunderstorms from "../../assets/images/PartlySunnythenChanceShowersAndThunderstorms.png";
import SlightChanceLightRainthenCloudy from "../../assets/images/SlightChanceLightRainthenCloudy.png";
import SlightChanceRainShowersthenMostlySunny from "../../assets/images/SlightChanceRainShowersthenMostlySunny.png";
import MostlySunnythenSlightChanceRainShowers from "../../assets/images/MostlySunnythenSlightChanceRainShowers.png";
import ChanceRainAndSnow from "../../assets/images/ChanceRainAndSnow.png";
import fogday from "../../assets/images/fogday.png";
import clearnight from "../../assets/images/clearnight.png";

const getWeatherIcon = (current: AV.IWeatherForcastData): string => {
  const conditionText = current.condition?.text || "";
  const isDaytime = current.is_day === 1;

  switch (conditionText) {
    case "Cloudy":
    case "Partly Cloudy":
      return PartlyCloudys;
    case "Fog":
    case "Patchy Fog":
    case "Widespread Fog":
    case "Areas Of Fog then Mostly Sunny":
    case "Areas Of Fog":
      return isDaytime ? fogday : PatchyFog;
    case "Thunderstorms":
    case "Showers And Thunderstorms":
    case "Slight Chance Showers And Thunderstorms then Partly Sunny":
    case "Chance Showers And Thunderstorms":
    case "Showers And Thunderstorms Likely":
      return ChanceShowersAndThunderstorms;
    case "Sunny":
    case "Mostly Sunny":
    case "Partly Sunny":
    case "Clear":
    case "Mostly Clear":
      return isDaytime ? MostlyClear : clearnight;
    case "Rain":
      return Rainy;
    case "Snow":
    case "Chance Snow Showers":
      return ChanceSnowShowers;
    case "Chance Rain And Snow":
      return ChanceRainAndSnow;
    case "Chance Rain Showers":
    case "Mostly Cloudy then Slight Chance Light Rain":
    case "Slight Chance Rain Showers":
      return SlightChanceRainShowers;
    case "Chance Snow Showers then Sunny":
      return ChanceSnowShowersthenSunny;
    case "Chance Rain And Snow then Cloudy":
      return ChanceRainAndSnowthenCloudy;
    case "Light Rain":
    case "Chance Light Rain":
    case "Slight Chance Light Rain":
      return SlightChanceLightRain;
    case "Slight Chance Light Rain then Cloudy":
      return SlightChanceLightRainthenCloudy;
    case "Light Rain then Rain And Snow":
      return ChanceRainAndSnow;
    case "Slight Chance Rain Showers then Mostly Sunny":
      return SlightChanceRainShowersthenMostlySunny;
    case "Partly Sunny then Chance Showers And Thunderstorms":
      return PartlySunnythenChanceShowersAndThunderstorms;
    case "Mostly Cloudy":
      return MostlyCloudy;
    case "Mostly Sunny then Slight Chance Rain Showers":
      return MostlySunnythenSlightChanceRainShowers;
    default:
      return isDaytime ? MostlyClear : clearnight;
  }
};

const EventTitle = ({
  entity,
  homeTeam,
  guestTeam,
  showGameInfo,
  leagueGameEntity,
  setIsImagePopup,
  setClickedUrl,
  setShowConfirmLinkPopup,
}) => {
  const params = useParams();
  const dispatch = useDispatch();
  const seriesId = params?.seriesId;
  const currentItem = seriesId
    ? entity?.seriesEvents?.find((se) => se.id === seriesId)
    : null;

  const start = Moment(currentItem?.start || entity?.start).format(
    "dddd, MMMM DD",
  );
  const end = Moment(currentItem?.end || entity?.end).format("dddd, MMMM DD");
  const startTime = Moment(currentItem?.start || entity?.start).format(
    "hh:mm A",
  );
  const endTime = Moment(currentItem?.end || entity?.end).format("hh:mm A");
  const arrivalTime = Moment(
    currentItem?.playerStart || entity?.playerStart,
  ).format("hh:mm A");
  const eventLocation = currentItem?.location || entity?.location;
  const url = currentItem?.locURL || entity?.locURL;

  const weatherData = useSelector((state: RootState) => state.user.weatherData);
  const [weatherLocation, setWeatherLocation] =
    useState<AV.IWeatherLocData | null>(null);
  const [displayWeather, setDisplayWeather] = useState<any>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchWeatherData = async () => {
      if (
        !eventLocation?.placeId ||
        !eventLocation?.geoData?.lat ||
        !eventLocation?.geoData?.lng
      )
        return;

      // Get cached weather from Redux
      const cachedWeather = weatherData.find(
        (item) => item.locId === eventLocation.id,
      );

      if (cachedWeather?.responseData?.current) {
        const cachedTime = new Date(cachedWeather.fetchedDate).getTime();
        const now = Date.now();
        const diffInMinutes = (now - cachedTime) / (1000 * 60);

        if (diffInMinutes < 30) {
          setDisplayWeather({
            ...cachedWeather.responseData.current,
            icon: getWeatherIcon(cachedWeather.responseData.current),
          });

          if (cachedWeather?.responseData?.location) {
            setWeatherLocation(cachedWeather.responseData.location);
          }

          return;
        }
      }

      try {
        const weatherResponse: AV.IWeatherData = await Location.getWeather(
          eventLocation.placeId,
          eventLocation.geoData.lat,
          eventLocation.geoData.lng,
        );

        if (!isMounted || !weatherResponse?.current) return;

        const fetchedDate = new Date().toISOString();

        let weatherItem = {
          locId: eventLocation.id,
          responseData: weatherResponse,
          fetchedDate,
        };

        let data = [
          ...weatherData.filter((w) => w.locId !== eventLocation.id),
          weatherItem,
        ];
        dispatch(setWeatherData(data));

        setDisplayWeather({
          ...weatherResponse.current,
          icon: getWeatherIcon(weatherResponse.current),
        });

        if (weatherResponse?.location) {
          setWeatherLocation(weatherResponse.location);
        }
      } catch (err) {
        if (isMounted) console.error("Failed to fetch weather:", err);
      }
    };

    fetchWeatherData();

    return () => {
      isMounted = false;
    };
  }, [eventLocation]);

  const FieldWidget = () => {
    return (
      <Tooltip label={eventLocation?.field?.name} openDelay={500}>
        <HStack space={2}>
          <Text fontSize={"16px"} color={"rgba(0, 0, 0, 0.50)"}>
            {Loc.currentLang.menu.fieldOrRoomText}{" "}
            <Text
              fontSize={16}
              width={"75%"}
              fontWeight={500}
              color={"black"}
              numberOfLines={1}
              textTransform={"capitalize"}
            >
              {eventLocation?.field?.name}
            </Text>
          </Text>
        </HStack>
      </Tooltip>
    );
  };

  const WeatheWidget = () => {
    const cityName = eventLocation?.normalized ?? "weather";
    //const cityName = weatherData?.location?.name ?? "weather";
    const googleWeatherUrl = `https://www.google.com/search?q=weather+today+${encodeURIComponent(
      cityName,
    )}`;

    return (
      <Pressable onPress={() => window.open(googleWeatherUrl, "_blank")}>
        <HStack alignItems={"center"} space={2}>
          <Text fontSize={"40px"}>
            {weatherLocation?.country === "United States of America"
              ? displayWeather?.temp_f
              : displayWeather?.temp_c}
          </Text>

          <Box alignSelf={"flex-end"}>
            <Image
              source={{
                uri: displayWeather?.icon,
              }}
              alt="rain"
              size="50px"
            />
          </Box>
        </HStack>
      </Pressable>
    );
  };

  const MapWidget = () => {
    return (
      <Pressable
        onPress={() =>
          window.open(
            "https://www.google.com/maps/dir/?api=1&destination=" +
              eventLocation?.normalized +
              "&destination_place_id=" +
              eventLocation?.placeId,
            "_blank",
            "noreferrer",
          )
        }
      >
        <Box w={350} h={150}>
          <Image
            source={{ uri: AV.Media.getMap(eventLocation) }}
            style={{
              height: "100%",
              width: "100%",
              borderRadius: 10,
              margin: "auto",
            }}
            alt="googlemap"
          />
        </Box>
      </Pressable>
    );
  };

  const ViewFieldOrRoomWidget = () => {
    return (
      <HStack>
        {entity?.location?.siteMap && (
          <HStack>
            <Pressable onPress={() => setIsImagePopup(true)}>
              <Text fontSize={"15px"} color={"#00A7BC"} fontWeight={"500"}>
                {eventLocation?.field?.name
                  ? Loc.currentLang.menu.viewFieldOrRoom
                  : ""}
              </Text>
            </Pressable>
          </HStack>
        )}
      </HStack>
    );
  };

  const LocationWidget = () => {
    return (
      <HStack
        flexWrap={"wrap"}
        width={"full"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <VStack width="40%">
          <HStack
            flexWrap="wrap"
            alignItems="center"
            space={2}
            justifyContent={"space-between"}
          >
            <HStack alignItems="flex-start" space={2} width="258%">
              <Box>
                <LocationIcon width={"28"} height={"30"} color={"#32AE78"} />
              </Box>
              <VStack width={"46%"}>
                <Text fontSize="18px" fontWeight="500" noOfLines={2}>
                  {eventLocation?.title}
                </Text>
                <Text fontSize="16px" fontWeight="200" noOfLines={2}>
                  {eventLocation?.normalized}
                </Text>
                <Pressable
                  onPress={() =>
                    window.open(
                      "https://www.google.com/maps/dir/?api=1&destination=" +
                        eventLocation?.normalized +
                        "&destination_place_id=" +
                        eventLocation?.placeId,
                      "_blank",
                      "noreferrer",
                    )
                  }
                >
                  <Text fontSize={"16px"} color={"#00A7BC"} fontWeight={400}>
                    {Loc.currentLang.menu.directions}
                  </Text>
                </Pressable>
                {displayWeather && <WeatheWidget />}
                {eventLocation?.field?.name && <FieldWidget />}
                {entity?.location?.siteMap && <ViewFieldOrRoomWidget />}
              </VStack>
            </HStack>
          </HStack>
        </VStack>

        <HStack alignSelf={"flex-end"}>
          <MapWidget />
        </HStack>
      </HStack>
    );
  };

  const EventLinkWidget = () => {
    return (
      <HStack>
        <Pressable
          onPress={() => {
            if (url) {
              setClickedUrl?.(url);
              setShowConfirmLinkPopup?.(true);
            }
          }}
        >
          <HStack space={2}>
            <LocationIcon width={"28"} height={"30"} color={"#32AE78"} />
            <Text
              paddingTop={"5px"}
              fontSize={"15px"}
              color={"#00A7BC"}
              fontWeight={"500"}
              underline
            >
              {Loc.currentLang.menu.eventLink}
            </Text>
          </HStack>
        </Pressable>
      </HStack>
    );
  };

  const EventTimeWidget = () => {
    return (
      <>
        <HStack w="100%" alignItems="flex-start" justifyContent="space-between">
          {/* LEFT DATE */}
          <Text fontSize="24px" alignSelf={"flex-end"}>
            {start}
          </Text>

          {/* RIGHT TIME SECTION */}
          <HStack
            flex={1}
            justifyContent="flex-end"
            alignItems="flex-start"
            space={8}
            flexWrap="wrap"
          >
            {/* START TIME */}
            <VStack minW="120px" space={1}>
              <Text color="gray.500" fontSize="15px">
                {Loc.currentLang.menu.eventStartTime}
              </Text>

              <HStack alignItems="center" space={2}>
                <TimerIcon width={"28"} height={"30"} color={"#32AE78"} />

                <Text fontSize="18px" fontWeight="500" color="#111827">
                  {startTime}
                </Text>
              </HStack>
            </VStack>

            {/* END TIME */}
            <VStack minW="120px" space={1}>
              <Text color="gray.500" fontSize="15px">
                {Loc.currentLang.menu.eventEndTime}
              </Text>

              <HStack alignItems="center" space={2}>
                <TimerIcon width={"28"} height={"30"} color={"#32AE78"} />

                <Text fontSize="18px" fontWeight="500" color="#111827">
                  {endTime}
                </Text>
              </HStack>
            </VStack>

            {/* ARRIVAL TIME */}
            {entity?.type === "Game" && arrivalTime && (
              <VStack minW="120px" space={1}>
                <Text color="gray.500" fontSize="15px">
                  {Loc.currentLang.menu.teamArrivalTime}
                </Text>

                <HStack alignItems="center" space={2}>
                  <TimerIcon width={"28"} height={"30"} color={"#32AE78"} />

                  <Text fontSize="18px" fontWeight="500" color="#111827">
                    {arrivalTime}
                  </Text>
                </HStack>
              </VStack>
            )}
          </HStack>
        </HStack>

        {entity?.profile?.text && <Text mt={3}>{entity?.profile?.text}</Text>}
      </>
    );
  };

  const LinksWidget = () => {
    return (
      <>
        {entity?.links &&
          entity?.links?.map((item, index) => (
            <Pressable
              key={index} // make sure to provide a unique key for each element in a list
              onPress={() => window.open(item?.url)}
              marginTop={"12px"}
              flexDirection={"row"}
              alignItems={"center"}
            >
              <img
                src={
                  item?.icon?.trim()?.length > 0
                    ? item?.icon
                    : "https://cdn-icons-png.flaticon.com/512/2985/2985013.png"
                }
                alt={`Icon ${index}`}
                height={"24px"}
                width={"24px"}
              />
              <Text ml={3} underline>
                {item?.url}
              </Text>
            </Pressable>
          ))}
      </>
    );
  };

  return (
    <Box
      marginBottom={
        entity?.type === "Game" &&
        entity?.teams?.length === 0 &&
        leagueGameEntity?.status === "Started"
          ? 0
          : 10
      }
    >
      {/* GAME INFO COLLAPSE */}
      {leagueGameEntity?.type === "LeagueGameBase" &&
        leagueGameEntity?.status === "Started" && (
          <>
            <Collapse
              isOpen={showGameInfo}
              height={/*!entity?.profile?.text ? "236px" : */ "300px"}
            >
              <VStack mt={0} px={6} space={4}>
                <EventTimeWidget />
                {url?.trim()?.length > 0 ? (
                  <EventLinkWidget />
                ) : (
                  <LocationWidget />
                )}
                {entity?.links?.length > 0 && <LinksWidget />}
              </VStack>
            </Collapse>

            <Divider thickness={1.5} />
          </>
        )}

      {/* NON-GAME EVENTS */}
      {(!leagueGameEntity || leagueGameEntity?.status !== "Started") && (
        <VStack mt={10} px={6} space={3}>
          <EventTimeWidget />
          {url?.trim()?.length > 0 ? <EventLinkWidget /> : <LocationWidget />}
          {entity?.links?.length > 0 && <LinksWidget />}
        </VStack>
      )}
    </Box>
  );
};

export default EventTitle;
